import { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react'
import { DotLoader } from 'react-spinners'
import type { SystemConfig, MaintenancePeriod } from '@common/models/config/system.modl'
import {
  isUnderMaintenance,
  getActiveMaintenancePeriod,
  getSystemConfigVersion,
  isRemoteVersionNewer,
} from '@common/models/config/system.modl'
import { SystemConfigService } from '@common/services/system-config-service'
import { VersionReloadDialog } from '@common/components/VersionReloadDialog'
import MaintenanceScreen from '@/views/error/maintenance'
import packageJson from '../../package.json'

type SystemConfigContextType = {
  /** config/system の内容（未取得・未作成時は null） */
  systemConfig: SystemConfig | null
  /** 現在メンテナンス中か */
  isMaintenance: boolean
  /** 現在有効なメンテナンス期間（なければ null） */
  activeMaintenancePeriod: MaintenancePeriod | null
  /** メンテナンスリストの最後の1件（表示用） */
  lastMaintenancePeriod: MaintenancePeriod | null
  /** 利用規約ページのURL（例: https://...） */
  termsOfServiceUrl: string
  isLoading: boolean
  error: Error | null
}

const SystemConfigContext = createContext<SystemConfigContextType>({
  systemConfig: null,
  isMaintenance: false,
  activeMaintenancePeriod: null,
  lastMaintenancePeriod: null,
  termsOfServiceUrl: '',
  isLoading: true,
  error: null,
})

/** メンテナンス判定を時刻の経過で再計算するための間隔（ms） */
const MAINTENANCE_CHECK_INTERVAL_MS = 60_000
const CURRENT_APP_VERSION = packageJson.version ?? '0.0.0'

/** config の取得状態（ログイン画面を出す前に必ず判定するため） */
type ConfigLoadStatus = 'loading' | 'no_doc' | 'loaded' | 'error'

export const SystemConfigProvider = ({ children }: { children: ReactNode }) => {
  const [systemConfig, setSystemConfig] = useState<SystemConfig | null>(null)
  const [loadStatus, setLoadStatus] = useState<ConfigLoadStatus>('loading')
  const [error, setError] = useState<Error | null>(null)
  const [now, setNow] = useState(() => new Date())

  const isLoading = loadStatus === 'loading'

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), MAINTENANCE_CHECK_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const service = new SystemConfigService()
    const unsubscribe = service.subscribeSystemConfig(
      (config) => {
        setSystemConfig(config)
        setError(null)
        setLoadStatus(config === null ? 'no_doc' : 'loaded')
      },
      (err) => {
        console.error('SystemConfig subscription error:', err)
        setError(err)
        setLoadStatus('error')
      },
    )
    return () => unsubscribe()
  }, [])

  const isMaintenance = useMemo(
    () => (systemConfig ? isUnderMaintenance(systemConfig, now) : false),
    [systemConfig, now]
  )
  const activeMaintenancePeriod = useMemo(
    () => (systemConfig ? getActiveMaintenancePeriod(systemConfig, now) : null),
    [systemConfig, now]
  )
  /** メンテナンスリストの最後の1件（表示用） */
  const lastMaintenancePeriod = useMemo(() => {
    if (!systemConfig?.maintenance?.length) return null
    return systemConfig.maintenance[systemConfig.maintenance.length - 1] ?? null
  }, [systemConfig])
  const termsOfServiceUrl = systemConfig?.termsOfService ?? ''
  const latestAppVersion = useMemo(
    () => (systemConfig ? getSystemConfigVersion(systemConfig, 'admin') : null),
    [systemConfig]
  )
  const shouldShowVersionReloadDialog = useMemo(
    () => !!(latestAppVersion && isRemoteVersionNewer(CURRENT_APP_VERSION, latestAppVersion)),
    [latestAppVersion]
  )

  const value: SystemConfigContextType = useMemo(
    () => ({
      systemConfig,
      isMaintenance,
      activeMaintenancePeriod,
      lastMaintenancePeriod,
      termsOfServiceUrl,
      isLoading,
      error,
    }),
    [
      systemConfig,
      isMaintenance,
      activeMaintenancePeriod,
      lastMaintenancePeriod,
      termsOfServiceUrl,
      isLoading,
      error,
    ]
  )

  // メンテナンス判定になったらどこにいても強制的にメンテナンス画面を表示。
  // ロード完了までログインを出さない（config 取得 → メンテナンス判定の順で表示）
  const maintenancePeriod = lastMaintenancePeriod ?? activeMaintenancePeriod
  const renderBody = () => {
    if (loadStatus === 'loading') {
      return (
        <div className="loading-overlay">
          <DotLoader color="var(--irogami-primary)" size={40} speedMultiplier={1} />
        </div>
      )
    }
    if (loadStatus === 'error') {
      return <MaintenanceScreen activeMaintenancePeriod={null} />
    }
    if (loadStatus === 'loaded' && isMaintenance) {
      return <MaintenanceScreen activeMaintenancePeriod={maintenancePeriod} />
    }
    return children
  }

  return (
    <SystemConfigContext.Provider value={value}>
      {renderBody()}
      {shouldShowVersionReloadDialog && latestAppVersion ? (
        <VersionReloadDialog
          currentVersion={CURRENT_APP_VERSION}
          latestVersion={latestAppVersion}
        />
      ) : null}
    </SystemConfigContext.Provider>
  )
}

export const useSystemConfig = () => {
  const context = useContext(SystemConfigContext)
  if (!context) {
    throw new Error('useSystemConfig must be used within a SystemConfigProvider')
  }
  return context
}
